<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3"
import { UiButton, UiCheckbox, UiCurrencyInput, UiFormLayout, UiSelect, UiTextInput } from "@/components"
import { useNotifications } from "@/composables/useNotifications"

type ProductPayload = {
  id?: number | null
  company_id: number | ""
  name: string
  sku: string
  kind: string
  measurement_unit_id: number | ""
  cost_price_cents: string
  can_be_purchased: boolean
}

const props = defineProps<{
  mode: "create" | "edit"
  title: string
  subtitle?: string
  product: ProductPayload
  companies: Array<{ id: number; name: string }>
  kinds: Array<{ label: string; value: string }>
  measurementUnits: Array<{ label: string; value: number }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  product: {
    company_id: props.product.company_id,
    name: props.product.name,
    sku: props.product.sku,
    kind: props.product.kind,
    measurement_unit_id: props.product.measurement_unit_id,
    cost_price_cents: props.product.cost_price_cents,
    can_be_purchased: props.product.can_be_purchased,
  },
})

const notify = useNotifications()
const fieldError = (field: string) => props.errors?.[field]?.[0]

function submit() {
  const opts = { onSuccess: () => notify.success(props.mode === "create" ? "Produto criado com sucesso" : "Produto atualizado com sucesso") }
  if (props.mode === "create") {
    form.post("/products", opts)
    return
  }

  form.put(`/products/${props.product.id}`, opts)
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/products" back-label="Voltar para produtos">
    <form class="space-y-4" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-2">
        <UiSelect
          id="product-company"
          :model-value="form.product.company_id ?? ''"
          label="Empresa"
          :options="companies.map((company) => ({ label: company.name, value: company.id }))"
          :disabled="mode === 'edit'"
          :error="fieldError('company') || fieldError('company_id')"
          @update:model-value="form.product.company_id = $event as number | ''"
        />

        <UiSelect
          id="product-kind"
          v-model="form.product.kind"
          label="Tipo"
          :options="kinds"
          :error="fieldError('kind')"
        />

        <UiSelect
          id="product-unit"
          :model-value="form.product.measurement_unit_id ?? ''"
          label="Unidade"
          :options="measurementUnits"
          :error="fieldError('measurement_unit') || fieldError('measurement_unit_id')"
          @update:model-value="form.product.measurement_unit_id = $event as number | ''"
        />

        <UiTextInput id="product-name" v-model="form.product.name" label="Nome" placeholder="Ex: Chapa de aco" :error="fieldError('name')" />

        <UiTextInput id="product-sku" v-model="form.product.sku" label="SKU" placeholder="Ex: MAT-001" :error="fieldError('sku')" />

        <UiCurrencyInput
          id="product-cost-price"
          v-model="form.product.cost_price_cents"
          label="Custo padrao"
          helper-text="Referencia de custo do produto (usada em calculos de composicao do produto)"
          :error="fieldError('cost_price')"
        />
      </div>

      <UiCheckbox
        id="product-can-be-purchased"
        v-model="form.product.can_be_purchased"
        label="Compravel"
        description="Marque quando o item puder ser comprado de fornecedor"
      />

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="submit" :disabled="form.processing">
          {{ mode === "create" ? "Criar produto" : "Salvar alteracoes" }}
        </UiButton>

        <Link href="/products" class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>


